import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { 
  Link2, 
  Copy, 
  Trash2, 
  Users, 
  FileText,
  Plus,
  Loader2,
  Check,
  ChevronDown,
  ChevronRight,
  Calendar,
  Activity
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminInvites({ token }) {
  const { t, i18n } = useTranslation();
  const [invites, setInvites] = useState([]);
  const [invitedAnalyses, setInvitedAnalyses] = useState([]);
  const [invitedUsers, setInvitedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newInviteNote, setNewInviteNote] = useState("");
  const [newInviteFreeAnalyses, setNewInviteFreeAnalyses] = useState(-1); // -1 = unlimited
  const [copiedId, setCopiedId] = useState(null);
  const [expandedUsers, setExpandedUsers] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      const [invitesRes, analysesRes] = await Promise.all([
        fetch(`${API}/admin/invites`, { credentials: "include", headers }),
        fetch(`${API}/admin/invited-analyses`, { credentials: "include", headers }),
      ]);

      if (invitesRes.ok) {
        const data = await invitesRes.json();
        setInvites(data.invites || []);
      }

      if (analysesRes.ok) {
        const data = await analysesRes.json();
        setInvitedAnalyses(data.analyses || []);
        setInvitedUsers(data.invited_users || []);
      }
    } catch (error) {
      console.error("Error fetching admin data:", error);
    } finally {
      setLoading(false);
    }
  };

  const createInvite = async () => {
    setCreating(true);
    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      const res = await fetch(`${API}/admin/invite`, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify({ 
          note: newInviteNote || null,
          free_analyses_count: newInviteFreeAnalyses
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setInvites([data, ...invites]);
        setShowCreateDialog(false);
        setNewInviteNote("");
        setNewInviteFreeAnalyses(-1);
        toast.success(t('admin.invites.createSuccess'));
        
        // Copy to clipboard
        await navigator.clipboard.writeText(data.invite_url);
        toast.success(t('admin.invites.copiedToClipboard'));
      } else {
        toast.error(t('admin.invites.createError'));
      }
    } catch (error) {
      toast.error(t('admin.invites.createError'));
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async (invite) => {
    await navigator.clipboard.writeText(invite.invite_url);
    setCopiedId(invite.invite_id);
    toast.success(t('admin.invites.linkCopied'));
    setTimeout(() => setCopiedId(null), 2000);
  };

  const deleteInvite = async (inviteId) => {
    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      const res = await fetch(`${API}/admin/invite/${inviteId}`, {
        method: "DELETE",
        credentials: "include",
        headers,
      });

      if (res.ok) {
        setInvites(invites.filter(i => i.invite_id !== inviteId));
        toast.success(t('admin.invites.deleteSuccess'));
      }
    } catch (error) {
      toast.error(t('admin.invites.deleteError'));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString(i18n.language === 'de' ? "de-DE" : "en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatDateLocal = (dateString) => {
    if (!dateString) return "-";
    const parts = dateString.split("-");
    if (parts.length === 3) {
      const year = parseInt(parts[0]);
      const month = parseInt(parts[1]) - 1;
      const day = parseInt(parts[2]);
      const date = new Date(year, month, day);
      return date.toLocaleDateString(i18n.language === 'de' ? "de-DE" : "en-US");
    }
    return new Date(dateString).toLocaleDateString(i18n.language === 'de' ? "de-DE" : "en-US");
  };

  // Group analyses by user
  const analysesByUser = invitedUsers.reduce((acc, user) => {
    acc[user.user_id] = invitedAnalyses.filter(a => a.user_id === user.user_id);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 text-volt animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Create Invite Section */}
      <div className="glass-card rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link2 className="h-5 w-5 text-volt" />
            <h2 className="font-display text-xl font-bold uppercase">{t('admin.invites.title')}</h2>
          </div>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="skew-btn bg-volt text-black hover:bg-volt/90 font-bold"
            data-testid="create-invite-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            <span>{t('admin.invites.createNew')}</span>
          </Button>
        </div>

        {invites.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">
            {t('admin.invites.noInvites')}
          </p>
        ) : (
          <div className="space-y-3">
            {invites.map((invite) => (
              <div 
                key={invite.invite_id}
                className={`p-4 rounded-lg border ${invite.used ? 'border-green-500/30 bg-green-500/5' : 'border-white/10 bg-white/5'}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {invite.used ? (
                        <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">
                          {t('admin.invites.used')}
                        </span>
                      ) : (
                        <span className="text-xs bg-volt/20 text-volt px-2 py-0.5 rounded">
                          {t('admin.invites.active')}
                        </span>
                      )}
                      {invite.note && (
                        <span className="text-sm text-muted-foreground truncate">
                          {invite.note}
                        </span>
                      )}
                      {invite.free_analyses_count !== undefined && (
                        <span className="text-xs bg-cyan/20 text-cyan px-2 py-0.5 rounded">
                          {invite.free_analyses_count === -1 ? t('admin.invites.unlimitedAnalyses') : t('admin.invites.analysesCount', { count: invite.free_analyses_count })}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate font-mono">
                      {invite.invite_url}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('admin.invites.createdAt')}: {formatDate(invite.created_at)}
                      {invite.used && invite.used_by_email && (
                        <span className="ml-2 text-green-400">
                          • {t('admin.invites.usedBy')}: {invite.used_by_email}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyLink(invite)}
                      className="hover:bg-white/10"
                      data-testid={`copy-invite-${invite.invite_id}`}
                    >
                      {copiedId === invite.invite_id ? (
                        <Check className="h-4 w-4 text-green-400" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                    {!invite.used && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteInvite(invite.invite_id)}
                        className="hover:bg-red-500/10 text-red-400"
                        data-testid={`delete-invite-${invite.invite_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Invited Users & Analyses */}
      <div className="glass-card rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <Users className="h-5 w-5 text-cyan" />
          <h2 className="font-display text-xl font-bold uppercase">{t('admin.invitedAnalyses.title')}</h2>
          <span className="text-sm text-muted-foreground">
            ({t('admin.invitedAnalyses.count', { analyses: invitedAnalyses.length, users: invitedUsers.length })})
          </span>
        </div>

        {invitedUsers.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">
            {t('admin.invitedAnalyses.noUsers')}
          </p>
        ) : (
          <div className="space-y-4">
            {invitedUsers.map((invUser) => {
              const userAnalyses = analysesByUser[invUser.user_id] || [];
              const isExpanded = expandedUsers[invUser.user_id];
              
              return (
                <div key={invUser.user_id} className="border border-white/10 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setExpandedUsers(prev => ({
                      ...prev,
                      [invUser.user_id]: !prev[invUser.user_id]
                    }))}
                    className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                    data-testid={`expand-user-${invUser.user_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-cyan/20 flex items-center justify-center">
                        <Users className="h-5 w-5 text-cyan" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium">{invUser.name || invUser.email}</p>
                        <p className="text-sm text-muted-foreground">{invUser.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-muted-foreground">
                        {userAnalyses.length} {userAnalyses.length === 1 ? t('admin.invitedAnalyses.analysisSingular') : t('admin.invitedAnalyses.analysisPlural')}
                      </span>
                      {isExpanded ? (
                        <ChevronDown className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                  </button>
                  
                  {isExpanded && userAnalyses.length > 0 && (
                    <div className="border-t border-white/10 bg-white/5">
                      {userAnalyses.map((analysis) => (
                        <div 
                          key={analysis.analysis_id}
                          className="p-4 border-b border-white/5 last:border-b-0 hover:bg-white/5"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="h-8 w-8 rounded bg-volt/20 flex items-center justify-center">
                                <Activity className="h-4 w-4 text-volt" />
                              </div>
                              <div>
                                <p className="font-medium">
                                  {analysis.athlete_name || t('admin.invitedAnalyses.unnamed')}
                                </p>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <Calendar className="h-3 w-3" />
                                  {formatDateLocal(analysis.athlete_data?.test_date || analysis.created_at?.split('T')[0])}
                                  <span className="text-volt/70">
                                    {analysis.test_type === "20min" ? t('admin.invitedAnalyses.test20min') : t('admin.invitedAnalyses.testRamp')}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-6 text-right">
                              <div>
                                <p className="text-volt font-mono text-lg">{analysis.ftp?.toFixed(0)}W</p>
                                <p className="text-xs text-muted-foreground">FTP</p>
                              </div>
                              <div>
                                <p className="text-cyan font-mono text-lg">{analysis.vo2max?.toFixed(1)}</p>
                                <p className="text-xs text-muted-foreground">VO2max</p>
                              </div>
                              <div>
                                <p className="font-mono text-lg">{analysis.ftp_per_kg?.toFixed(2)}</p>
                                <p className="text-xs text-muted-foreground">W/kg</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Invite Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader>
            <DialogTitle className="font-display uppercase">{t('admin.invites.createDialog.title')}</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label htmlFor="invite-note" className="text-sm text-muted-foreground">
                {t('admin.invites.createDialog.noteLabel')}
              </Label>
              <Input
                id="invite-note"
                value={newInviteNote}
                onChange={(e) => setNewInviteNote(e.target.value)}
                placeholder={t('admin.invites.createDialog.notePlaceholder')}
                className="mt-2 bg-white/5 border-white/10"
                data-testid="invite-note-input"
              />
            </div>
            
            <div>
              <Label htmlFor="invite-analyses" className="text-sm text-muted-foreground">
                {t('admin.invites.createDialog.analysesLabel')}
              </Label>
              <div className="mt-2 flex items-center gap-3">
                <select
                  id="invite-analyses"
                  value={newInviteFreeAnalyses}
                  onChange={(e) => setNewInviteFreeAnalyses(parseInt(e.target.value))}
                  className="flex-1 h-10 rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-volt"
                  data-testid="invite-analyses-select"
                >
                  <option value={-1}>{t('admin.invites.createDialog.unlimited')}</option>
                  <option value={0}>{t('admin.invites.createDialog.noFreeAnalyses')}</option>
                  <option value={1}>{t('admin.invites.createDialog.oneAnalysis')}</option>
                  <option value={2}>{t('admin.invites.createDialog.nAnalyses', { count: 2 })}</option>
                  <option value={3}>{t('admin.invites.createDialog.nAnalyses', { count: 3 })}</option>
                  <option value={5}>{t('admin.invites.createDialog.nAnalyses', { count: 5 })}</option>
                  <option value={10}>{t('admin.invites.createDialog.nAnalyses', { count: 10 })}</option>
                  <option value={20}>{t('admin.invites.createDialog.nAnalyses', { count: 20 })}</option>
                  <option value={50}>{t('admin.invites.createDialog.nAnalyses', { count: 50 })}</option>
                </select>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {newInviteFreeAnalyses === -1 
                  ? t('admin.invites.createDialog.unlimitedDesc')
                  : newInviteFreeAnalyses === 0
                    ? t('admin.invites.createDialog.noFreeDesc')
                    : t('admin.invites.createDialog.limitedDesc', { count: newInviteFreeAnalyses })
                }
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateDialog(false)}
              className="border-white/20"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={createInvite}
              disabled={creating}
              className="bg-volt text-black hover:bg-volt/90"
              data-testid="confirm-create-invite"
            >
              {creating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t('admin.invites.createDialog.create')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
